import React, { useCallback, useEffect, useRef, useState } from "react";
import { sortBy } from "lodash";

import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Stack,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { ArrowUpward, ArrowDownward } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";

import { HelpBox } from "@/components/atoms";
import { PenPortraitCompareInsight, PenPortraitInsightVariable } from "@/types";
import { toIndex, toPercentage, whiteOverlay } from "@/utils";

interface PenPortraitCompareCardProps {
  title: string;
  variables: PenPortraitInsightVariable[];
  comparison?: PenPortraitCompareInsight;
  score: string;
  showSectionComparison: boolean;
}

const PenPortraitCompareCard: React.FC<PenPortraitCompareCardProps> = ({
  title,
  variables,
  comparison,
  score,
  showSectionComparison = false
}) => {
  const theme = useTheme();

  const [scores, setScores] = useState<PenPortraitInsightVariable[]>([]);
  const [overallScore, setOverallScore] = useState<number | undefined>();
  const formatFunc = score === "rate" ? toPercentage : toIndex;

  function useElementWidth() {
    const [width, setWidth] = useState<number>(275);
    const observerRef = useRef<ResizeObserver | null>(null);

    const ref = useCallback((node: HTMLElement | null) => {
      if (observerRef.current) observerRef.current.disconnect();

      if (node) {
        const observer = new ResizeObserver(([entry]) => {
          setWidth(entry.contentRect.width);
        });

        observer.observe(node);
        observerRef.current = observer;
      }
    }, []);

    // Cleanup
    useEffect(() => {
      return () => {
        if (observerRef.current) {
          observerRef.current.disconnect();
        }
      };
    }, []);

    return [ref, width] as const;
  }

  // Get the size of the card
  const [cardRef, cardWidth] = useElementWidth();

  // Calculate the size of the images
  const numImages = cardWidth > 264 ? 6 : 4
  const imageSize = Math.max(Math.floor((cardWidth - 32) / numImages) - 8, 32);

  useEffect(() => {
    let filtered = variables
      .filter((x) =>
        score === "rate"
          ? Number(x[score as keyof PenPortraitInsightVariable]) > 0
          : Number(x[score as keyof PenPortraitInsightVariable]) > 0,
      );
    setScores(sortBy(filtered, score).reverse());
  }, [score, variables]);

  useEffect(() => {
    setOverallScore(
      scores
        .slice(0, numImages)
        .reduce((sum, item) => sum + Number(item[score as keyof PenPortraitInsightVariable]), 0) / numImages
    )
  }, [scores, score])

  if (scores.length === 0) {
    return null;
  } else {
    return (
      <Grid size={12} key={title}>
        <Card
          ref={cardRef}
          sx={{
            border: 1,
            borderRadius: 1,
            borderColor: theme.palette.divider,
            height: `${56 + 32 + imageSize + 34}px`,
            position: "relative",
          }}
        >
          <CardHeader
            disableTypography
            title={
              <Box
                display="flex"
                justifyContent="space-between"
              >
                <Typography variant="body2">
                  {title}
                </Typography>
                {overallScore && (
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="center"
                      spacing={1}
                    >
                      <HelpBox
                        title={
                          `The average score for variables in this section, a higher
                          value means variables in this section are more important 
                          to the audience.`
                        }
                      >
                        <Typography variant="body2">
                          {formatFunc(overallScore)}
                        </Typography>
                      </HelpBox>
                      {comparison && comparison.score_diff !== 0 && showSectionComparison && (
                        <HelpBox title="How the average score for this section compares to the other audience">
                          <Stack direction="row" spacing={0}>
                            {comparison.score_diff > 0 ? (
                              <ArrowUpward fontSize="small" color={comparison.score_diff > 0 ? "success" : "error"}/>
                            ) : (
                              <ArrowDownward fontSize="small" color={comparison.score_diff > 0 ? "success" : "error"}/>
                            )}
                            <Typography variant="body2" color={comparison.score_diff > 0 ? "success" : "error"}>
                              {toPercentage(Math.abs(comparison.score_diff), 0)}
                            </Typography>
                          </Stack>
                        </HelpBox>
                      )}
                    </Stack>
                )}
              </Box>
            }
            sx={{ height: "56px", alignItems: "flex-start", pb: 0 }}
          />
          <CardContent sx={{ pb: 2, "&:last-child": { pb: 2 } }}>
            <Grid container spacing={1} justifyContent="center">
              {scores.slice(0, numImages).map((variable) => {

                const hide = comparison && comparison.overlapping.includes(variable.tag_id);

                return (
                  <Grid key={`${title}-${variable.tag_id}`}>
                    <HelpBox
                      title={`${variable.tag_name} (${formatFunc(
                        Number(
                          variable[score as keyof PenPortraitInsightVariable],
                        ),
                      )})`}
                    >
                      <Stack spacing={1} sx={{ width: imageSize }}>
                        <Box
                          position="relative"
                          height={imageSize}
                          width={imageSize}
                          sx={{
                            // borderRadius: "5px",
                            overflow: "hidden",
                            backgroundColor: hide ? "#000" : "transparent"
                          }}
                        >
                          {hide && (
                            <Box
                              height={imageSize + 1}
                              width={imageSize + 1}
                              sx={{
                                position: "absolute",
                                inset: 0,
                                backgroundColor: "#000",
                                opacity: 0.8,
                                zIndex: 1,
                                boxShadow: "inset 0 0 0 1px #000",
                                // borderRadius: "inherit"
                              }}
                            />
                          )}
                          <Box
                            component="img"
                            src={variable.image}
                            alt={variable.tag_name}
                            height="100%"
                            width="100%"
                            display="block"
                            style={{
                              borderRadius: hide ? "0px" : "5px",
                              padding: variable.tag_type === "Interests" ? imageSize / 10 : undefined,
                              backgroundColor: theme.palette.background.paper,
                              backgroundImage: variable.tag_type === "Interests" ? whiteOverlay(0.15) : undefined,
                            }}
                          />

                        </Box>
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: "8px",
                            textAlign: "center",
                            display: "-webkit-box",
                            overflow: "hidden",
                            WebkitBoxOrient: "vertical",
                            WebkitLineClamp: 2,
                          }}
                        >
                          {variable.tag_name}
                        </Typography>
                      </Stack>
                    </HelpBox>
                  </Grid>
                )}
              )}
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    );
  }
};

export default PenPortraitCompareCard;
